// Manual sections 24–27: the short playbooks per role, the numbers and statuses worth
// keeping to hand, the common questions, and the glossary. See ../index.js for the schema.

const ALL_ROLES = ['sAdm', 'adm', 'qa', 'callAgent'];

const reference = [
  {
    id: 'playbooks',
    group: 'Reference',
    title: 'Role playbooks',
    lede: 'The short version, one page per job.',
    roles: ALL_ROLES,
    blocks: [
      { type: 'h3', text: 'A QA Agent’s day' },
      {
        type: 'steps',
        items: [
          {
            title: 'Open Assigned to me.',
            text: 'These are the calls assigned to you, 25 at a time. Filter by campaign, and by `All Calls`, `Assessed` or `Not Assessed`. A call opens in a new tab.',
          },
          {
            title: 'Read the Overview',
            text: 'to orient yourself.',
          },
          {
            title: 'Listen to the recording',
            text: 'on the `Transcript` tab, following the transcript as you go.',
          },
          {
            title: 'Work through the QA Assessment',
            text: 'question by question, checking the AI’s answer, reason and evidence against what you heard.',
          },
          {
            title: 'Override what is wrong.',
            text: 'Click `Override`, correct the questions, then `Save Changes`. Every override is recorded in the Audit log.',
          },
          {
            title: 'Mark as Assessed.',
            text: 'The call is locked (`Assessed & Locked`) and the agent can now dispute it.',
          },
          {
            title: 'Check the Disputes page',
            text: 'for anything contested. See {{Disputes|#disputes}}.',
          },
        ],
      },
      { type: 'h3', text: 'A Call Agent’s week' },
      {
        type: 'steps',
        items: [
          {
            title: 'Open My Calls.',
            text: 'The calls filed under your agent name, 25 at a time. Filter by campaign.',
          },
          {
            title: 'Look at the Assessed column',
            text: 'to see which calls a reviewer has been through.',
          },
          {
            title: 'Open an assessed call',
            text: 'and read each question’s answer, reason and evidence on the `QA Assessment` tab.',
          },
          {
            title: 'Use the Generative AI Query panel',
            text: 'to ask questions about the call.',
          },
          {
            title: 'Dispute it if you disagree.',
            text: 'Click `Dispute`, give your reason and `Submit Dispute`. The call shows `Disputed — pending review` until a reviewer resolves it; then you see the outcome and their reason.',
          },
        ],
      },
      {
        type: 'p',
        text:
          'A **General Manager** also gets a **Your team** panel on My Calls. Pick a name under `Viewing calls for` to see that agent’s calls, and `Back to my calls` to return. A General Manager can dispute their team’s calls as well as their own. `Manage my team` opens Agent Management, where `Add to my team` takes on an agent who has no General Manager and `Remove from my team` lets one go. A Junior Manager always needs a General Manager, so only a Super Admin can move one.',
      },
      { type: 'h3', text: 'An Admin setting up from scratch' },
      {
        type: 'steps',
        items: [
          {
            title: 'Build the scorecard first',
            text: 'on Scorecards: the scoring system, then each question with its type, Expected Criteria and weightings. See {{Scorecards|#scorecards}}.',
          },
          {
            title: 'Create the campaign',
            text: 'on Campaigns, link the scorecard, and attach any Knowledge Base Files the AI should score against.',
          },
          {
            title: 'Bring recordings in',
            text: 'with Upload Recordings (`File Upload` or `Fetch from Genesys`), the Cloud Connector, or a Scheduled Job.',
          },
          {
            title: 'Watch the job',
            text: 'through to `Complete` on the Jobs page.',
          },
          {
            title: 'Assign calls',
            text: 'from Campaign Home: tick calls, then `Assign` → `Assign to me` or `Choose QA agent…`.',
          },
        ],
      },
      {
        type: 'p',
        text:
          'People are a **Super Admin** job. Create console users in User Management; a user given the Call Agent role gets an agent record straight away. Then, in Agent Management, use `Set Details` or `Edit Details` to give an agent an email, a role (General Manager, Junior Manager or QA Assessor) and the General Manager they report to (anyone except a General Manager can report to one), and to link their login when their username differs from the name on their calls.',
      },
    ],
  },

  {
    id: 'reference',
    group: 'Reference',
    title: 'Tables & limits',
    lede: 'The numbers and formats worth keeping to hand.',
    roles: ALL_ROLES,
    blocks: [
      {
        type: 'table',
        caption: 'Limits and formats',
        head: ['Thing', 'Value'],
        rows: [
          ['Supported audio formats', 'WAV, MP3, M4A, FLAC'],
          ['Maximum file name length', '200 characters'],
          ['Files that make a batch upload', 'More than 5 at once'],
          ['Genesys date range', 'Required; maximum 7 days; defaults to the last 7 days'],
          ['Genesys filters always applied', 'Voice calls only, answered calls only'],
          ['ANI / DNIS format', '`tel:+[number]`'],
          ['Scheduled job name length', '10 characters maximum'],
          ['Scheduled job times', 'Entered in SAST, stored as UTC'],
          ['QA Weighting (Yes/No scoring)', 'Quality questions must total exactly 100%'],
          ['Compliance Weighting (Yes/No scoring)', 'Quality questions must total exactly 100%'],
          ['Calls per page (Search, Assigned to me, My Calls)', '25; `Load More Calls` fetches the next 25'],
          ['Search with no date range', 'The last 5 days only, unless you search by GUID, Conversation ID, DNIS or job'],
          ['Agents per page (Agent Management)', '30; `Load More Agents` fetches the next 30. No total is shown.'],
          ['Paused jobs listed on Transcribe Quota', 'The last 7 days'],
        ],
      },
      {
        type: 'table',
        caption: 'Statuses you will see',
        head: ['Status', 'Where', 'Meaning'],
        rows: [
          ['`Processing`', 'Jobs', 'The batch is still being worked through.'],
          ['`Complete`', 'Jobs', 'Every call processed successfully.'],
          ['`Completed with failures`', 'Jobs', 'The batch finished, but some calls failed. Open it to see which.'],
          ['`Queued`', 'Job Detail', 'Waiting to start.'],
          ['`Transcribing`', 'Job Detail', 'Audio is being turned into text.'],
          ['`Processing`', 'Job Detail', 'Analysis and scoring are running.'],
          ['`Failed`', 'Job Detail', 'This call could not be processed.'],
          ['`Quota Stopped`', 'Job Detail', 'Paused at the daily call limit; a Super Admin can resume it.'],
          ['`Assessed` / `Not Assessed`', 'Call lists', 'Whether a reviewer has marked the call as assessed.'],
          ['`Assessed & Locked`', 'Call page', 'Assessed; no further overrides or refreshes.'],
          ['`Non-sales`', 'Call page', 'Marked non-sales, so it can be left out of scores.'],
          ['`Disputed — pending review`', 'Call page (Call Agents)', 'You have disputed the call and a reviewer has not resolved it yet.'],
          ['`Pending`', 'Disputes', 'A dispute waiting for a reviewer.'],
          ['`Resolved — Upheld`', 'Disputes, call lists', 'Reviewed; the assessment stands.'],
          ['`Resolved — Changed`', 'Disputes, call lists', 'Reviewed; the assessment was corrected.'],
        ],
      },
    ],
  },

  {
    id: 'troubleshooting',
    group: 'Reference',
    title: 'Troubleshooting',
    lede: 'The things people ask about most, and what to do about each.',
    roles: ALL_ROLES,
    blocks: [
      {
        type: 'defs',
        items: [
          {
            term: 'My Calls is empty, or shows the wrong calls',
            text: 'Calls are filed under the agent name on the call. If your username is different, a Super Admin links your login to that agent in Agent Management (`Edit Details` → `Login`).',
          },
          {
            term: 'Someone is missing from my team',
            text: 'General Managers: open `Manage my team` and click `Add to my team` next to them. If they are already on another General Manager’s team, ask a Super Admin to move them.',
          },
          {
            term: 'A batch seems stuck and nothing is progressing',
            text: 'Open the job and look for calls at `Quota Stopped`. Those are paused at the daily call limit, not failed. A Super Admin can raise today’s limit and resume them from {{Transcribe Quota|#quota}}.',
          },
          {
            term: 'A page in this manual is not in my sidebar',
            text: 'Your role does not include it. Check {{Who sees what|#roles}}.',
          },
          {
            term: 'I can’t open User Management',
            text: 'It is for Super Admins only, and the server enforces that too.',
          },
          {
            term: 'The Dispute button is missing on my call',
            text: 'Only Call Agents dispute, and only calls that are assessed. A call can be disputed once; after that the outcome shows on the call instead.',
          },
          {
            term: 'I can’t override or refresh a call',
            text: 'It is `Assessed & Locked`. To change it during a dispute, use `Change Assessment`; otherwise a Super Admin can `Unmark as Assessed`.',
          },
          {
            term: 'Reviewer overrides disappeared from a call',
            text: 'The call was re-scored, with `Refresh` or `Swap Agent / Customer`, which replaces manual overrides with fresh AI results. The Audit log still holds every override.',
          },
          {
            term: 'Agent and customer are the wrong way round',
            text: 'Use `Swap Agent / Customer` on the `Transcript` tab. It is offered only on a two-speaker Agent/Customer call that is not yet assessed, and it re-scores the call.',
          },
          {
            term: 'A call shows no QA score',
            text: 'It was scored on a compliance-only scorecard. Those calls pass or fail on compliance and have no QA score.',
          },
          {
            term: 'A question is marked N/A on calls it should have assessed',
            text: 'The question is set as `Not always applicable` and its pre-check steps did not hold on that call. Reword the steps on {{Scorecards|#scorecards}}.',
          },
          {
            term: 'The same calls are being QA’d twice from a schedule',
            text: 'The scan window is longer than the run frequency. Narrow the window or run less often. The form shows an **Overlap Warning** and asks you to confirm before it saves. See {{Scheduled Jobs|#scheduled-jobs}}.',
          },
          {
            term: 'A schedule is missing days of calls',
            text: 'The scan window is shorter than the gap between runs. The form calls this **Data Sampling Mode** and lets it save, because it is sometimes intended. Widen the window if you wanted full coverage.',
          },
          {
            term: 'I can’t rename a campaign',
            text: 'Campaign names can’t be changed after creation. Create a new campaign with the right name, and `Deactivate` the old one rather than deleting it, so its history stays.',
          },
          {
            term: 'Search can’t find an older call',
            text: 'With no date range, Search looks back only 5 days. Set a `Date Range`, or search by GUID, Conversation ID, DNIS or job. Results come 25 at a time; use `Load More Calls` for more. See {{Search|#search}}.',
          },
          {
            term: 'Agent Management shows no total, and a filter misses people',
            text: 'Agents load 30 at a time and no total is shown. The role and login filter only narrows the agents already loaded, so use `Load More Agents` to bring in more matches.',
          },
          {
            term: 'I no longer get a weekly performance email',
            text: 'The weekly per-agent report emails are switched off.',
          },
        ],
      },
    ],
  },

  {
    id: 'glossary',
    group: 'Reference',
    title: 'Glossary',
    roles: ALL_ROLES,
    blocks: [
      {
        type: 'defs',
        items: [
          {
            term: 'Agent Behavior and Conduct',
            text: 'A pass/fail question type about how the agent behaved, shown as **Conduct** in tables and reported separately from the QA score.',
          },
          {
            term: 'Assessed',
            text: 'A call a reviewer has marked as done. It is locked, and the agent can dispute it.',
          },
          {
            term: 'Campaign',
            text: 'The group a call belongs to, with the scorecards its calls are assessed against and any knowledge base files.',
          },
          {
            term: 'Compliance question',
            text: 'A pass/fail check with a priority (Low, Medium or High), reported separately from the QA score.',
          },
          {
            term: 'Compliance-only scorecard',
            text: 'A scorecard where every question is a compliance check. Calls pass or fail on compliance and have no QA score.',
          },
          {
            term: 'Critical Fail',
            text: 'A question marked so that the wrong answer fails the whole call, whatever the score.',
          },
          {
            term: 'Dispute',
            text: 'A Call Agent’s (or their General Manager’s) challenge to an assessed call, with a reason. A reviewer resolves it as upheld or changed.',
          },
          {
            term: 'Evidence',
            text: 'The quotes from the transcript behind a question’s answer.',
          },
          {
            term: 'Expected Criteria',
            text: 'What good looks like for a question.',
          },
          {
            term: 'General Manager',
            text: 'An agent role set in Agent Management. A General Manager sees and can dispute their team’s calls, and manages their own team.',
          },
          {
            term: 'Job',
            text: 'One batch of recordings brought in together.',
          },
          {
            term: 'Junior Manager',
            text: 'An agent role that must report to a General Manager.',
          },
          {
            term: 'Knowledge base files',
            text: 'Reference documents attached to a campaign or scorecard for the AI to score against.',
          },
          {
            term: 'Non-sales',
            text: 'A flag a reviewer puts on a call, with an optional reason, so it can be left out of scores.',
          },
          {
            term: 'Not always applicable',
            text: 'A Compliance or Conduct question with pre-check steps. If the steps do not hold, the question is recorded as N/A, neither a pass nor a fail.',
          },
          {
            term: 'Override',
            text: 'A reviewer’s correction of the AI’s result on a question. Always written to the Audit log.',
          },
          {
            term: 'Per-agent sampling',
            text: 'QA’ing a set number of calls per agent instead of every call, across the whole window or per day. Blank means all calls.',
          },
          {
            term: 'QA score',
            text: 'The call’s score from its Quality questions, each counted by its weighting.',
          },
          {
            term: 'Quality question',
            text: 'A weighted question that counts towards the QA score.',
          },
          {
            term: 'Scorecard',
            text: 'The set of questions a call is assessed against. Some filters call it a **Template**.',
          },
          {
            term: 'Scoring system',
            text: 'How a scorecard is scored: Yes/No Scoring (v2.0), Numeric Scoring (v1.0 - Legacy) or Compliance-only.',
          },
          {
            term: 'Success Answer',
            text: 'Whether "Yes" or "No" is the good answer to a question.',
          },
        ],
      },
    ],
  },
];

export default reference;
